/**
 * Component: Cart Item Upsell
 * -----------------------------------------------------------------------------
 * Adds related or complimentary products to the mini cart.
 */
import axios from 'axios'
import { getLocalURL } from '../../../scripts/helpers/globale'
import classes from '~/config/classes'
import { getCart } from '../../../scripts/helpers/cart'
import { throttleTapMobile } from '../../../scripts/helpers/quick-add'

export default class CartItemUpsell {
  constructor(component) {
    this.dom = {
      $component: component,
      $container: document.querySelector('.js-cartItemUpsellWrapper'),
    }
    this.state = {
      upsellId: null,
      settings: window.basis.theme.cartUpsell || null,
      quickAddWrapClass: '.js-product-card-quick-add-mobile',
      touchStart: false,
      attemptIndex: 0,
      currentProductId: null,
    }

    this.mount()
    this.setEventListeners()
    this.bindToWindow()
  }

  async mount() {
    const cartData = await getCart()
    const { items } = cartData
    if (items.length === 0) {
      return
    }

    const productId = items[0].product_id
    this.getRecommendations(productId)
  }

  /**
   * Set event listeners
   * - listens for events and calls getRecommendations
   */
  setEventListeners() {
    window.basis.EventBus.listen('product-added', (id) => {
      const productId = id.productId
      if (this.matchCurrentId(productId)) {
        return
      }
      this.getRecommendations(productId)
    })

    window.basis.EventBus.listen('cart-updated', (cart) => {
      const { items } = cart.data
      if (items.length === 0) {
        this.toggleShowUpsell(false)
        this.resetCurrentProductId()
        return
      }

      const productId = items[0].product_id
      if (this.matchCurrentId(productId)) {
        return
      }
      this.getRecommendations(productId)
    })
  }

  /**
   * Checks if the current product id matches the id passed
   * - used to determine if a new call to getRecommendations is needed
   */
  matchCurrentId(id) {
    const { state } = this
    if (!state.currentProductId || state.currentProductId.toString() !== id.toString()) {
      return false
    }

    return true
  }

  /**
   * Get product from recommendations api
   * - recursively calls itself if first attempt returns no products
   * - max two calls for related and/or complementary products
   * @param {String|Number} id - product id
   */
  async getRecommendations(id) {
    const { state } = this
    const { types, limit } = state.settings
    this.toggleShowUpsell(false)
    const intent = types[state.attemptIndex]

    if (!intent) {
      this.resetAttempts()
      this.resetCurrentProductId()
      return
    }

    const url = getLocalURL(`/recommendations/products?product_id=${id}&limit=${limit}&section_id=cart-item-upsell&intent=${intent}`)

    try {
      const response = await axios.get(url)
      const container = document.createElement('div')
      container.innerHTML = response.data
      const upsellElement = container.querySelector('.js-cartItemUpsell')
      const upsellItems = upsellElement.querySelectorAll('.js-cartItemUpsell-item')

      if (upsellItems.length === 0) {
        if (state.attemptIndex === 1) {
          this.resetAttempts()
          return
        }

        state.attemptIndex += 1
        this.getRecommendations(id)
        return
      }

      this.appendUpsellToCart(upsellElement, id)
    } catch (err) {
      console.log('Error getting product recommendations', err)
    }
  }

  /**
   * Appends the upsell element to the mini cart
   * @param {HTMLElement} element - upsell element
   */
  appendUpsellToCart(element, id) {
    const { dom, state } = this
    const mountCards = new Event('mountproductcards')
    dom.$container.innerHTML = element.innerHTML
    document.dispatchEvent(mountCards)
    this.loadUpsellImages()
    this.updateMobileEventListeners()
    this.toggleShowUpsell(true)
    this.resetAttempts()
    state.currentProductId = id
  }

  /**
   * Resets the attempt index
  */
  resetAttempts() {
    const { state } = this
    state.attemptIndex = 0
  }

  /**
   * Resets the current product id
   */
  resetCurrentProductId() {
    const { state } = this
    state.currentProductId = null
  }

  /**
   * Toggles the upsell visibility
   */
  toggleShowUpsell(active = false) {
    const { dom } = this
    if (active) {
      dom.$component.classList.add(classes.active)
      return
    }

    dom.$component.classList.remove(classes.active)
  }

  /**
   * Adds event listeners to quick add buttons on mobile
   * - this is a UX enhancement to allow users to see the quick add options on
   *  mobile without accidently adding to cart on touchstart.
   * - button reveals options on touchstart so it remains point events none
   * until touchend then is deactivate after click
   */
  updateMobileEventListeners() {
    const { dom, state } = this
    const elements = dom.$container.querySelectorAll(state.quickAddWrapClass)
    throttleTapMobile(elements)
  }

  /**
   * Loads the images for the upsell items
   */
  loadUpsellImages() {
    const { dom } = this
    const images = dom.$container.querySelectorAll('.js-cartItemImg')

    images.forEach((image) => this.loadImage(image))
  }

  /**
   * Loads an image
   * @param {HTMLElement} image - image element
   */
  async loadImage(image) {
    const { src } = image.dataset
    const img = new Image()
    img.src = src
    await new Promise((resolve) => img.onload = () => resolve())
    image.setAttribute('src', src)
    image.setAttribute('data-loaded', true)
  }

  bindToWindow() {
    window.theme = window.theme || {}
    window.theme.MiniCartUpsell = this
  }
}
